import React from 'react';
import { Text, RichText, Image } from '@sitecore-jss/sitecore-jss-react';
import { AdsCard, AdsCardContent } from '@arm/arm-design-system-react';

import './styles.css';
import './ctalink.css';

import { addStyle, getTrackingAttributes } from '../SharedUtils/utils';

const renderCard = (fields, isLight, isEditorMode, index) => {
  const carouselCard = fields;

  const carouselCardClassList = ['c-carousel-card', 'c-carousel-card--description-text-ellipses'];
  const carouselCardStyle = {};
  const cardCTAClassList = ['js-cta', 'c-button', 'c-carousel-card-button'];
  const cardCTALinkClassList = ['js-cta', 'c-carousel-card-link', 'c-cta__link'];

  let cardUrl = '';
  let cardTarget = '';
  let cardLabel = '';
  let dataTrack = '';
  let cardCTABtnLabel = '';
  let cardCTALinkLabel = '';
  let cardCTABtnAltText = '';
  let cardCTALinkAltText = '';

  if (carouselCard.CallToActionLink?.value?.href && carouselCard.CallToAction?.value?.href) {
    cardUrl = carouselCard.CallToAction?.value?.href;
    cardTarget = carouselCard.CallToAction?.value?.target;
    cardLabel = `${carouselCard.CallToAction?.value?.title} / ${carouselCard.CallToActionLink?.value?.title}`;
    dataTrack = `carousel:CTA:${carouselCard.CallToAction?.value?.text}`;

    cardCTABtnLabel = carouselCard.CallToAction?.value?.text;
    cardCTALinkLabel = carouselCard.CallToActionLink?.value?.text;
    cardCTABtnAltText = carouselCard.CallToAction?.value?.title;
    cardCTALinkAltText = carouselCard.CallToActionLink?.value?.title;
  } else if (carouselCard.CallToActionLink?.value?.href) {
    cardUrl = carouselCard.CallToActionLink?.value?.href;
    cardTarget = carouselCard.CallToActionLink?.value?.target;
    cardLabel = carouselCard.CallToActionLink?.value?.title;
    dataTrack = `carousel:CTA:${carouselCard.CallToActionLink?.value?.text}`;

    cardCTALinkLabel = carouselCard.CallToActionLink?.value?.text;
    cardCTALinkAltText = carouselCard.CallToActionLink?.value?.title;
  } else if (carouselCard.CallToAction?.value?.href) {
    cardUrl = carouselCard.CallToAction?.value?.href;
    cardTarget = carouselCard.CallToAction?.value?.target;
    cardLabel = carouselCard.CallToAction?.value?.title;
    dataTrack = `carousel:CTA:${carouselCard.CallToAction?.value?.text}`;

    cardCTABtnLabel = carouselCard.CallToAction?.value?.text;
    cardCTABtnAltText = carouselCard.CallToAction?.value?.title;
  }

  if (carouselCard.TextAlignTop?.value) carouselCardClassList.push("c-carousel-card--content-align-top");
  if (carouselCard.CallToActionAlignBottom?.value) carouselCardClassList.push("c-carousel-card-cta--bottomAlign");
  if (carouselCard.HeadlineImageCenterAlign?.value) carouselCardClassList.push("c-carousel-card--headline-image-centerAlign");
  if (carouselCard.disableEllipses?.value) carouselCardClassList.splice(carouselCardClassList.indexOf("c-carousel-card--description-text-ellipses"), 1);
  if (carouselCard.TextAlignCentered?.value) carouselCardClassList.push("c-carousel-card--centerAlign");
  if (carouselCard.CallToActionAlignCenter?.value) carouselCardClassList.push("c-carousel-card-cta--centerAlign");

  const isInvertColor = isLight
    ? carouselCard.InvertedColorLight?.value
    : carouselCard.InvertedColorDark?.value;

  if (isInvertColor) carouselCardClassList.push('is-inverted');

  const isInvertCtaButton = isLight
    ? carouselCard.InvertedCTAButtonLight?.value
    : carouselCard.InvertedCTAButtonDark?.value;

  if (isInvertCtaButton) {
    cardCTAClassList.push('c-button--cta-invert');
    cardCTALinkClassList.push('c-cta__link--invert');
  } else {
    cardCTAClassList.push('c-button--cta');
    cardCTALinkClassList.push('c-link--cta');
  }

  const backgroundImageField = isLight
    ? carouselCard.BackgroundImageLight
    : carouselCard.BackgroundImageDark;

  if (backgroundImageField?.value?.src && backgroundImageField?.value?.class !== 'scEmptyImage') {
    addStyle(
      carouselCardStyle,
      '--carousel-card-background-image',
      `url(${backgroundImageField.value.src})`
    );
  }

  addStyle(
    carouselCardStyle,
    '--carousel-card-background-color',
    isLight ? carouselCard.BackgroundColorLight?.value : carouselCard.BackgroundColorDark?.value
  );
  addStyle(
    carouselCardStyle,
    '--carousel-card-border-color',
    isLight ? carouselCard.BorderColorLight?.value : carouselCard.BorderColorDark?.value
  );
  addStyle(
    carouselCardStyle,
    '--carousel-card-border-hover-color',
    isLight ? carouselCard.BorderHoverColorLight?.value : carouselCard.BorderHoverColorDark?.value
  );
  addStyle(carouselCardStyle, '--carousel-card-height', carouselCard.Height?.value);
  addStyle(
    carouselCardStyle,
    '--carousel-card-opacity',
    isLight ? carouselCard.OpacityLight?.value : carouselCard.OpacityDark?.value
  );

  addStyle(
    carouselCardStyle,
    '--carousel-card-cta-link-color',
    isLight ? carouselCard.ctaLinkColorLight?.value : carouselCard.ctaLinkColorDark?.value
  );
  addStyle(
    carouselCardStyle,
    '--carousel-card-cta-link-hover-color',
    isLight ? carouselCard.ctaLinkHoverColorLight?.value : carouselCard.ctaLinkHoverColorDark?.value
  );
  addStyle(
    carouselCardStyle,
    '--carousel-card-cta-link-visited-color',
    isLight
      ? carouselCard.ctaLinkVisitedColorLight?.value
      : carouselCard.ctaLinkVisitedColorDark?.value
  );

  if (carouselCard.numberLineEllipses?.value > 0) {
    addStyle(
      carouselCardStyle,
      '--carousel-card-ellipses-line-clamp',
      carouselCard.numberLineEllipses.value
    );
  }

  const headlineImageSrc = carouselCard.HeadlineImage?.value?.src;
  const badgeImageSrc = carouselCard.BadgeImage?.value?.src;

  const trackingAttributes = getTrackingAttributes(fields);

  const renderHeadline = () => {
    const strippedValue = removeParagraphs(carouselCard.HeadlineRTE?.value);

    switch (carouselCard.HeadlineThirdType?.value) {
      case 'H3':
        return (
          <div className="c-carousel-card__title u-text-lg-3 u-text-md-3 u-text-sm-3">
            <h3>
              {isEditorMode && <RichText field={carouselCard.HeadlineRTE} />}
              {!isEditorMode && <RichText field={carouselCard.HeadlineRTE} />}
            </h3>
          </div>
        );
      case 'H4':
        return (
          <div className="c-carousel-card__title u-text-lg-4 u-text-md-4 u-text-sm-4">
            <h4>
              {isEditorMode && <RichText field={carouselCard.HeadlineRTE} />}
              {!isEditorMode && <RichText field={carouselCard.HeadlineRTE} />}
            </h4>
          </div>
        );
      default:
        return (
          <div className="c-carousel-card__title u-text-lg-3 u-text-md-3 u-text-sm-3">
            {isEditorMode && <RichText field={carouselCard.HeadlineRTE} />}
            {!isEditorMode && <RichText field={carouselCard.HeadlineRTE} />}
          </div>
        );
    }
  };

  const removeParagraphs = (text) => {
    return text?.replace('<p>', '').replace('</p>', '');
  };

  return (
    <div className="tns-item" key={index}>
      <AdsCard className="carousel-ads-card">
        <AdsCardContent slot="content" className="c-image-above-text-card__content">
          <a
            href={cardUrl || undefined}
            target={cardTarget || undefined}
            aria-label={cardLabel || undefined}
            title={cardLabel || undefined}
            role="link"
            className={carouselCardClassList.join(' ')}
            style={carouselCardStyle}
            {...trackingAttributes}
          >
            {(isEditorMode || headlineImageSrc) && (
              <div className="c-carousel-card__headline-image">
                <Image field={carouselCard.HeadlineImage} tabIndex="0" />
              </div>
            )}

            {carouselCard.showBadge?.value && (
              <>
                {carouselCard.BadgeImageOnly?.value && (
                  <div className="c-carousel-card__badge">
                    <div className="c-carousel-card__badge-icon">
                      {(isEditorMode || badgeImageSrc) && (
                        <Image field={carouselCard.BadgeImage} tabIndex="0" />
                      )}
                    </div>
                  </div>
                )}

                {!carouselCard.BadgeImageOnly?.value && (
                  <div className="c-carousel-card__badge">
                    <div className="c-carousel-card__badge-icon is-defined-size">
                      {(isEditorMode || badgeImageSrc) && (
                        <Image field={carouselCard.BadgeImage} tabIndex="0" />
                      )}
                    </div>
                    <div className="c-carousel-card__badge-text">
                      {(isEditorMode || carouselCard.BadgeTitle?.value) && (
                        <div className="c-carousel-card__badge-title">
                          <Text field={carouselCard.BadgeTitle} />
                        </div>
                      )}

                      {(isEditorMode || carouselCard.BadgeSubTitle?.value) && (
                        <div className="c-carousel-card__badge-subtitle">
                          <Text field={carouselCard.BadgeSubTitle} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {renderHeadline()}

            {(isEditorMode || carouselCard.SubTitle?.value) && (
              <div className="c-carousel-card__subtitle">
                <Text field={carouselCard.SubTitle} />
              </div>
            )}

            {(isEditorMode || carouselCard.Description?.value) && (
              <div className="c-carousel-card__description">
                <RichText field={carouselCard.Description} />
              </div>
            )}

            {cardCTABtnLabel && (
              <div className="c-carousel-card__CTA">
                <div
                  className={cardCTAClassList.join(' ')}
                  role="button"
                  aria-label={cardCTABtnAltText}
                  title={cardCTABtnAltText}
                  {...trackingAttributes}
                >
                  {cardCTABtnLabel}
                </div>
              </div>
            )}

            {cardCTALinkLabel && (
              <div
                className={cardCTALinkClassList.join(' ')}
                role="button"
                aria-label={cardCTALinkAltText}
                title={cardCTALinkAltText}
              >
                <span>
                  {cardCTALinkLabel}
                  <span
                    className="c-cta__link__icon fal fa-chevron-right"
                    aria-hidden="true"
                  ></span>
                </span>
              </div>
            )}
          </a>
        </AdsCardContent>
      </AdsCard>
    </div>
  );
};

export default renderCard;
